import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ArrowRight, FileText, RefreshCw, Zap } from "lucide-react";
import PullToRefresh from "react-simple-pull-to-refresh";
import { getVirtualStatus } from "../../../utility/VirtualStatus";
import "./ActiveGamesDashboard.css";

const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/games`;
const ACTIVE_API_URL = `${API_BASE_URL}/active`;
const FORMER_API_URL = `${API_BASE_URL}/former`;

const groupByContext = (games) => {
  const grouped = {};
  games.forEach((game) => {
    game.user_teams.forEach((userTeam) => {
      const playerIgn = userTeam.player_ign || userTeam.name;
      const key = `${game.clan_id}_${playerIgn}`;
      if (!grouped[key]) {
        grouped[key] = {
          clan_name: game.clan_name,
          player_ign: playerIgn,
          games: [],
        };
      }
      if (!grouped[key].games.some((g) => g.id === game.id)) {
        grouped[key].games.push(game);
      }
    });
  });
  return Object.values(grouped);
};

function GamesDashboard() {
  const navigate = useNavigate();
  const [isRefreshing, setIsRefreshing] = useState(false);

  // State for Active Games
  const [activeGames, setActiveGames] = useState([]);
  const [activeLoading, setActiveLoading] = useState(true);
  const [activeError, setActiveError] = useState(null);
  const [groupedActiveGames, setGroupedActiveGames] = useState([]);

  // State for Former Games
  const [formerGames, setFormerGames] = useState([]);
  const [formerLoading, setFormerLoading] = useState(true);
  const [formerError, setFormerError] = useState(null);
  const [groupedFormerGames, setGroupedFormerGames] = useState([]);

  const fetchAllData = useCallback(async () => {
    setIsRefreshing(true);
    // We fetch both simultaneously for better performance
    const activePromise = axios.get(ACTIVE_API_URL, { withCredentials: true });
    const formerPromise = axios.get(FORMER_API_URL, { withCredentials: true });

    try {
      const [activeRes, formerRes] = await Promise.all([
        activePromise,
        formerPromise,
      ]);

      const aGames = activeRes.data.games || [];
      setActiveGames(aGames);
      setGroupedActiveGames(groupByContext(aGames));
      setActiveError(null);

      const fGames = formerRes.data.games || [];
      setFormerGames(fGames);
      setGroupedFormerGames(groupByContext(fGames));
      setFormerError(null);
    } catch (err) {
      console.error("Error refreshing dashboard:", err);
      setActiveError("Failed to refresh data.");
    } finally {
      setActiveLoading(false);
      setFormerLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  // Initial load only - auto-refresh intervals removed as requested
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const handleGoToGame = (clanId, eventId, gameId) => {
    navigate(`/games/clan/${clanId}/events/${eventId}/game/${gameId}`);
  };

  const renderPlayerStates = (teamStates, userTeams) => {
    const userTeamIds = new Set(userTeams.map((t) => t.id));
    return teamStates.map((state) => {
      const isUserTeam = userTeamIds.has(state.team_id);
      return (
        <div
          key={state.team_id}
          className={`team-state-item ${isUserTeam ? "user-team-item" : ""}`}
          style={{ color: state.team_color }}
        >
          {state.team_name} (Pos: {state.current_position})
          {isUserTeam ? " ⭐" : ""}
        </div>
      );
    });
  };

  const renderGameTable = (
    title,
    groupedData,
    loading,
    error,
    isFormer = false,
  ) => {
    if (loading && !isRefreshing) {
      return (
        <div className="loading-message">
          <Zap size={16} className="spin-icon" /> Loading {title}...
        </div>
      );
    }

    if (error) {
      return (
        <div className="error-message">
          <strong>Error:</strong> {error}
        </div>
      );
    }

    return (
      <div className="games-table-container">
        <div className="dashboard-header-block">
          <h2>{title}</h2>
          <button
            className="btn-refresh-manual"
            onClick={fetchAllData}
            disabled={isRefreshing}
          >
            <RefreshCw size={18} className={isRefreshing ? "spin-icon" : ""} />
          </button>
        </div>

        {groupedData.length === 0 ? (
          <div className="empty-state-message">
            {isFormer
              ? "No finished games on record."
              : "No active games found."}
          </div>
        ) : (
          <table>
            <thead>
              <tr className="main-header-row">
                <th>Game / Event</th>
                <th>Status</th>
                <th>Teams & Positions</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {groupedData.map((group) => (
                <React.Fragment key={group.clan_name + group.player_ign}>
                  <tr className="group-context-row">
                    <td colSpan="4">
                      <div className="context-wrapper">
                        <span className="context-label">Clan:</span>&nbsp;
                        <strong>{group.clan_name}</strong>
                        <span className="context-separator">&nbsp;|&nbsp;</span>
                        <span className="context-label">Playing As:</span>&nbsp;
                        <strong className="player-ign-display">
                          {group.player_ign}
                        </strong>
                      </div>
                    </td>
                  </tr>
                  {group.games.map((game) => {
                    const displayStatus = getVirtualStatus(game);
                    return (
                      <tr key={game.id} className="game-detail-row">
                        <td className="game-cell">
                          <strong>{game.event_name}</strong>
                          <div className="game-board-info">
                            Board: {game.board_size} sq.
                          </div>
                        </td>
                        <td>
                          <strong
                            className={`game-status-badge status-${displayStatus}`}
                          >
                            {displayStatus.toUpperCase()}
                          </strong>
                        </td>
                        <td className="teams-cell">
                          {renderPlayerStates(
                            game.team_states,
                            game.user_teams,
                          )}
                        </td>
                        <td className="action-cell">
                          <button
                            className={`btn-action ${isFormer ? "btn-success" : "btn-primary"}`}
                            onClick={() =>
                              handleGoToGame(
                                game.clan_id,
                                game.event_id,
                                game.id,
                              )
                            }
                          >
                            {isFormer ? (
                              <>
                                <FileText size={18} />
                                <span className="button-text">
                                  View Results
                                </span>
                              </>
                            ) : (
                              <>
                                <ArrowRight size={18} />
                                <span className="button-text">Go To Game</span>
                              </>
                            )}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  };

  return (
    <div className="games-dashboard-wrapper">
      <p className="mobile-refresh-hint">↓ Pull down to refresh all games</p>

      <PullToRefresh onRefresh={fetchAllData} pullingContent="">
        <div className="refresh-wrapper">
          {renderGameTable(
            "Active Games",
            groupedActiveGames,
            activeLoading,
            activeError,
          )}
          <div className="section-separator"></div>
          {renderGameTable(
            "Former Games",
            groupedFormerGames,
            formerLoading,
            formerError,
            true,
          )}
        </div>
      </PullToRefresh>
    </div>
  );
}

export default GamesDashboard;
